/* ==========================================================================
   BRASSLUXE MOROCCO — shared motion runtime (classic script, defer)
   Exposes window.BrassLuxe used by every section ES module:
     BrassLuxe.reducedMotion   bool — prefers-reduced-motion
     BrassLuxe.isMobile        bool — <768px or coarse pointer
     BrassLuxe.particleScale   0..1 — from theme settings
     BrassLuxe.gsap / .ScrollTrigger / .lenis
     BrassLuxe.mount(selector, setup) — init on load + Theme Editor
       section:load, auto-destroy on section:unload.
       setup(el, ctx) may return a cleanup function.
     BrassLuxe.reveal(scope)   — wire [data-reveal] children via IO
     BrassLuxe.magnetic(el)    — magnetic hover (desktop, setting-gated)
   Section scripts MUST be type="module" and call init immediately
   (modules run after deferred classics, so BrassLuxe is ready).
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobile = window.matchMedia('(max-width: 767px)').matches ||
    window.matchMedia('(pointer: coarse)').matches;

  if (reduced) root.classList.add('no-motion');

  var BLX = {
    reducedMotion: reduced,
    isMobile: mobile,
    particleScale: reduced ? 0 : (parseFloat(root.getAttribute('data-particle-density')) || 0) / 100,
    smoothScrollEnabled: root.getAttribute('data-smooth-scroll') === 'true',
    magneticEnabled: root.getAttribute('data-magnetic') === 'true',
    gsap: window.gsap || null,
    ScrollTrigger: window.ScrollTrigger || null,
    lenis: null,
    _cleanups: new Map()
  };

  /* ---------- GSAP + Lenis wiring ---------- */
  if (BLX.gsap && BLX.ScrollTrigger) {
    BLX.gsap.registerPlugin(BLX.ScrollTrigger);
  }

  if (BLX.smoothScrollEnabled && !reduced && typeof window.Lenis === 'function') {
    try {
      BLX.lenis = new window.Lenis({ duration: 1.15, smoothWheel: true });
      if (BLX.gsap) {
        BLX.gsap.ticker.add(function (time) { BLX.lenis.raf(time * 1000); });
        BLX.gsap.ticker.lagSmoothing(0);
        if (BLX.ScrollTrigger) BLX.lenis.on('scroll', BLX.ScrollTrigger.update);
      } else {
        var raf = function (time) { BLX.lenis.raf(time); requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
      }
    } catch (e) { BLX.lenis = null; }
  }

  /* ---------- reveal system: [data-reveal] ---------- */
  var revealObserver = null;
  if ('IntersectionObserver' in window && !reduced) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
  }

  BLX.reveal = function (scope) {
    var els = (scope || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    els.forEach(function (el, i) {
      if (revealObserver) {
        if (el.hasAttribute('data-reveal-stagger') && !el.style.getPropertyValue('--reveal-delay')) {
          el.style.setProperty('--reveal-delay', (i * 0.09) + 's');
        }
        var rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.94) {
          el.classList.add('is-revealed');
        } else {
          revealObserver.observe(el);
        }
      } else {
        el.classList.add('is-revealed');
      }
    });
  };

  /* ---------- magnetic hover ---------- */
  BLX.magnetic = function (el, strength) {
    if (mobile || reduced || !BLX.magneticEnabled) return;
    var s = strength || 0.28;
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      var r = el.getBoundingClientRect();
      var x = (e.clientX - r.left - r.width / 2) * s;
      var y = (e.clientY - r.top - r.height / 2) * s;
      el.style.transform = 'translate(' + x + 'px,' + y + 'px)';
    });
    el.addEventListener('pointerleave', function () {
      el.style.transition = 'transform .45s cubic-bezier(.22,1,.36,1)';
      el.style.transform = '';
      setTimeout(function () { el.style.transition = ''; }, 460);
    });
  };

  /* ---------- section mount helper (Theme Editor safe) ---------- */
  BLX.mount = function (selector, setup) {
    function initEl(el) {
      if (BLX._cleanups.has(el)) return;
      var cleanup = setup(el, BLX);
      if (typeof cleanup === 'function') BLX._cleanups.set(el, cleanup);
    }
    document.querySelectorAll(selector).forEach(initEl);

    document.addEventListener('shopify:section:load', function (e) {
      var el = e.target.querySelector ? e.target.querySelector(selector) : null;
      if (el) initEl(el);
      BLX.reveal(e.target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var el = e.target.querySelector ? e.target.querySelector(selector) : null;
      if (el && BLX._cleanups.has(el)) {
        BLX._cleanups.get(el)();
        BLX._cleanups.delete(el);
      }
    });
  };

  /* ---------- lazy module loader (viewport-gated dynamic import) ---------- */
  BLX.lazyImport = function (el, url, onLoad) {
    if (!('IntersectionObserver' in window)) {
      import(url).then(onLoad);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.disconnect();
          import(url).then(onLoad).catch(function () {});
        }
      });
    }, { rootMargin: '200px 0px' });
    io.observe(el);
  };

  /* ---------- global reveal boot ---------- */
  function boot() { BLX.reveal(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }

  window.BrassLuxe = BLX;
})();
